import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, FormHelperText, TextField, Select, MenuItem, Alert, Divider } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { CustomPhoneInput } from '../../utils/customPhoneInput';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/sites/addSiteDialog.css';

export const AddSiteDialog = ({ setSites, setTotalSites, setFilteredSitesCount, openAddSite, setOpenAddSite, setSiteId }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const authenticatedUser = useSelector(state => state.AuthenticatedUser);

    const [regions, setRegions] = useState([]);
    const [errMsg, setErrMsg] = useState('');

    const formik = useFormik({

        initialValues: {
            name: '',
            address: '',
            address_line_2: '',
            phone_number: '',
            region_id: authenticatedUser?.user_region_ids?.length === 1 ? authenticatedUser?.user_region_ids[0] : '',
            postal_code: '',
            city: '',
            description: '',
        },

        validationSchema: Yup.object().shape({
            name: Yup.string().required(t('isRequired')),
            address: Yup.string().optional(),
            region_id: Yup.string().required(t('isRequired')),
            postal_code: Yup.string().optional(),
            city: Yup.string().optional(),
            description: Yup.string().optional(),
            address_line_2: Yup.string().optional(),
            phone_number: Yup.string().optional(),
        }),

        onSubmit: async (values) => {
            try {
                // Send the FormData object via Axios
                const response = await axios.post('site', values);

                if (response.status === 200 || response.status === 201) {
                    setOpenAddSite(false);
                    setSiteId(response.data.id);
                    setSites(prevSites => [...prevSites, response.data]);
                    setTotalSites(prevTotal => prevTotal + 1);
                    setFilteredSitesCount(prevCount => prevCount + 1);
                    navigate(`site_details/${response.data.id}`);
                }

            } catch (error) {
                console.error('Error adding site:', error);
                CustomErrorLogger(error, 'Error adding site in AddSiteDialog');
                setErrMsg(error?.response?.data?.message || error.message || 'Erreur lors de l\'ajout du site');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, setFieldValue, touched, errors } = formik;

    const handleClose = useCallback(() => setOpenAddSite(false), [setOpenAddSite]);

    const fetchRegions = useCallback(async () => {
        try {
            const response = await axios.get('region');
            if (response.status === 200 || response.status === 201) {
                setRegions(response.data);
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            CustomErrorLogger(error, "error fetching regions in addSiteDialoge");
        }
    }, []);

    return (
        <Dialog open={openAddSite} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addSite_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addSite_close_icon' />
            </IconButton>
            <DialogTitle className='addSite_dialog_title'>Ajouter un site</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addSite_dialog_content'>
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Nom de site</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez le titre'
                            value={values.name}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                        />
                    </Box>
                    {authenticatedUser.user_region_ids.length > 1 &&
                        <Box className='addSite_helper_selct_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">{t('Region')}</FormHelperText>
                            <Select
                                className={`addSite_select_metier`}
                                value={values.region_id}
                                onChange={(e) => { handleChange(e) }}
                                onBlur={handleBlur}
                                onOpen={fetchRegions}
                                name="region_id"
                                error={touched.region_id && Boolean(errors.region_id)}
                            >
                                {regions?.map((region) => (
                                    <MenuItem
                                        key={region.id} className='addSite_select_metier_item' value={region.id}>
                                        {region.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {touched.region_id && errors.region_id && (
                                <FormHelperText error className='addSite_select_helper'>{errors.region_id}</FormHelperText>
                            )}
                        </Box>
                    }
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Description (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez la description'
                            value={values.description}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="description"
                            error={touched.description && Boolean(errors.description)}
                            helperText={touched.description && errors.description}
                        />
                    </Box>
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Adresse (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder="Entrez l'adresse"
                            value={values.address}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="address"
                            error={touched.address && Boolean(errors.address)}
                            helperText={touched.address && errors.address}
                        />
                    </Box>
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Complément d'adresse (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder="Entrez l'adresse"
                            value={values.address_line_2}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="address_line_2"
                            error={touched.address_line_2 && Boolean(errors.address_line_2)}
                            helperText={touched.address_line_2 && errors.address_line_2}
                        />
                    </Box>
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Postal code (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez postal code'
                            value={values.postal_code}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="postal_code"
                            error={touched.postal_code && Boolean(errors.postal_code)}
                            helperText={touched.postal_code && errors.postal_code}
                        />
                    </Box>
                    <Box className="addSite_helper_field_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteInputTitle">Ville (optionnel)</FormHelperText>
                        <TextField
                            fullWidth
                            placeholder='Entrez la ville'
                            value={values.city}
                            className='addSite_InputField'
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="city"
                            error={touched.city && Boolean(errors.city)}
                            helperText={touched.city && errors.city}
                        />
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText id="outlined-weight-helper-text" className="addUserInputTitle">Numéro Tel (optionnel)</FormHelperText>
                        <CustomPhoneInput
                            name="phone_number"
                            value={values.phone_number}
                            onChange={(value) => setFieldValue('phone_number', value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions className='addSite_dialog_actions'>
                    <Button className='addSite_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addSite_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};